import { useEffect, useRef } from "react";
import { useScroll, useMotionValueEvent } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { MARQUEE } from "@/data/content";

const PREMISE_VIDEO = "/videos/premise.mp4";

export const About = () => {
  const trackRef = useRef(null);
  const videoRef = useRef(null);
  const targetRef = useRef(0);

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    targetRef.current = Math.min(Math.max(p, 0), 0.999);
  });

  useEffect(() => {
    let raf;
    const tick = () => {
      const v = videoRef.current;
      if (v && v.duration && v.readyState >= 1 && !v.seeking) {
        const target = targetRef.current * v.duration;
        const diff = target - v.currentTime;
        if (Math.abs(diff) > 0.01) {
          v.currentTime =
            Math.abs(diff) > 1.5 ? target : v.currentTime + diff * 0.3;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    let url;
    let cancelled = false;
    fetch(PREMISE_VIDEO)
      .then((r) => r.blob())
      .then((b) => {
        if (cancelled) return;
        url = URL.createObjectURL(b);
        const v = videoRef.current;
        if (v) {
          v.src = url;
          v.load();
        }
      })
      .catch(() => {});
    const unlock = () => {
      const v = videoRef.current;
      if (!v) return;
      const p = v.play();
      if (p) p.then(() => v.pause()).catch(() => {});
    };
    window.addEventListener("touchstart", unlock, { once: true, passive: true });
    return () => {
      cancelled = true;
      window.removeEventListener("touchstart", unlock);
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  return (
    <section
      id="about"
      data-testid="about-section"
      className="bg-background overflow-visible"
    >
      <div ref={trackRef} className="relative h-[300vh]">
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 lg:gap-16 items-center">
              <div>
                <Reveal>
                  <p className="overline mb-5 sm:mb-8">The Premise</p>
                </Reveal>
                <Reveal delay={0.1}>
                  <h2 className="font-display font-light text-2xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-foreground">
                    The most compelling property film already exists – inside
                    the photography you have commissioned.
                  </h2>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="mt-5 sm:mt-10 max-w-2xl text-muted-foreground text-sm sm:text-lg leading-relaxed">
                    Form Pathway gives still architecture motion. Using advanced
                    AI to derive depth, parallax and movement, then refined
                    frame by frame by human editors, we craft listing films with
                    the pace and poise of cinema. The result feels directed,
                    never automated - built for audiences who notice the
                    difference.
                  </p>
                </Reveal>
              </div>
              <div className="flex justify-center sm:justify-end">
                <div
                  data-testid="premise-video-wrap"
                  className="w-full max-w-[34rem] max-h-[82vh] aspect-[720/780] overflow-hidden bg-background"
                >
                  <video
                    ref={videoRef}
                    data-testid="premise-video"
                    muted
                    playsInline
                    preload="none"
                    poster="/images/premise_poster.jpg"
                    onLoadedMetadata={(e) => {
                      const v = e.target;
                      const p = v.play();
                      if (p) p.then(() => v.pause()).catch(() => {});
                    }}
                    className="w-full h-full object-cover pointer-events-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Reveal delay={0.2} className="pb-6 sm:pb-8">
        <div className="relative border-y border-border py-7 overflow-hidden">
          <div className="flex w-max marquee-track">
            {[...MARQUEE, ...MARQUEE].map((item, i) => (
              <span
                key={i}
                className="font-display text-3xl sm:text-4xl text-foreground/30 px-8 sm:px-12 whitespace-nowrap"
              >
                {item}
                <span className="text-accent ml-8 sm:ml-12">/</span>
              </span>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default About;
